/**
 * @file apps/web/src/lib/latestUpload.ts
 * @description Decides what the Discovery page should do with a project's most
 * recent recording upload (GET /audio/latest) when the page loads — so an
 * upload in progress, or one that just finished, is still visible after a
 * refresh instead of vanishing with the component state that tracked it.
 *
 *   resume  — still processing and recent enough to be genuinely running:
 *             open the upload panel and resume polling.
 *   stalled — still "processing" long after it started. The pipeline runs
 *             inside the API process, so a redeploy or restart mid-job leaves
 *             the row in 'transcribing'/'extracting' forever; polling it would
 *             spin indefinitely. Surface it as needing a re-upload instead.
 *   recent  — finished (done / failed / extraction error) within the last 24
 *             hours: show a one-line status strip with a way to expand it.
 *   none    — no upload, or an old finished one (nothing worth surfacing).
 *
 * Pure functions, no React — unit-tested.
 */

import type { AudioUploadDetail } from './api.js';

export type UploadStage = AudioUploadDetail['stage'];
export type LatestUploadKind = 'resume' | 'stalled' | 'recent' | 'none';

export const ACTIVE_STAGES: readonly UploadStage[] = ['uploaded', 'transcribing', 'extracting', 'matching'];

/** A long workshop recording can legitimately take a while; past this it is presumed lost. */
export const ACTIVE_MAX_AGE_MS = 60 * 60 * 1000;
/** How long a finished upload's status strip stays on the page. */
export const RECENT_WINDOW_MS = 24 * 60 * 60 * 1000;

type LatestLike = Pick<AudioUploadDetail, 'stage' | 'uploaded_at'>;

export function classifyLatestUpload(upload: LatestLike | null, now: number = Date.now()): LatestUploadKind {
  if (!upload) return 'none';
  const uploadedAt = Date.parse(upload.uploaded_at);
  if (Number.isNaN(uploadedAt)) return 'none';
  const age = now - uploadedAt;

  if (ACTIVE_STAGES.includes(upload.stage)) return age <= ACTIVE_MAX_AGE_MS ? 'resume' : 'stalled';
  return age <= RECENT_WINDOW_MS ? 'recent' : 'none';
}

/** One line for the status strip: what the latest recording's pipeline did. */
export function describeLatestUpload(
  upload: Pick<AudioUploadDetail, 'stage' | 'filename' | 'discovery_suggestions_created'>,
  kind: LatestUploadKind,
): string {
  if (kind === 'stalled') return `${upload.filename} — processing appears to have stopped. Please upload it again.`;
  switch (upload.stage) {
    case 'done': {
      const n = upload.discovery_suggestions_created ?? 0;
      return `${upload.filename} — processing complete, ${n} ${n === 1 ? 'suggestion' : 'suggestions'} to review`;
    }
    case 'requirements_error':
      return `${upload.filename} — transcript saved, but requirement extraction failed`;
    case 'error':
      return `${upload.filename} — processing failed`;
    default:
      return `${upload.filename} — processing`;
  }
}
